import React from 'react';
import { Alert, AlertTitle, Button, Stack } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionAccessContext } from '../contexts/SubscriptionAccessContext';
import { RouteTo } from '@routing/RouteMap';

/**
 * SubscriptionRequiredAlert – lightweight banner that informs the user that
 * their company currently has **no active subscription** and guides them to
 * the shop/checkout flow.
 *
 * Usage examples:
 *   1) Stand-alone on a page: <SubscriptionRequiredAlert />
 *   2) Gate around feature UI:
 *        <SubscriptionRequiredAlert gate>
 *          <PremiumFeature />
 *        </SubscriptionRequiredAlert>
 *
 * The `gate` prop replaces the children with the alert when access is missing.
 */
interface Props {
  /**
   * When true and user lacks access, the alert REPLACES the children.
   * When false, the alert is rendered above the children.
   * Defaults to false.
   */
  gate?: boolean;
  children?: React.ReactNode;
}

const SubscriptionRequiredAlert: React.FC<Props> = ({ gate = false, children }) => {
  const navigate = useNavigate();
  const { hasAccess, loading } = useSubscriptionAccessContext();

  // Still loading → render nothing to avoid flicker
  if (loading) return null;

  const goToShop = () => {
    navigate(RouteTo.SHOP); // adjust route constant as needed
  };

  // User has subscription → either render children or nothing
  if (hasAccess) {
    return <>{children}</>;
  }

  const alertNode = (
    <Alert icon={<WarningAmberIcon />} severity="warning" sx={{ mb: 2 }}>
      <AlertTitle>Kein aktives Abonnement</AlertTitle>
      Du benötigst ein aktives Abonnement, um diese Funktion zu nutzen.
      <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
        <Button variant="contained" size="small" onClick={goToShop}>
          Abo abschließen
        </Button>
      </Stack>
    </Alert>
  );

  // gate = true → replace children entirely
  if (gate) {
    return alertNode;
  }

  // Otherwise show alert + children (disabled logic can be added later)
  return (
    <>
      {alertNode}
      {children}
    </>
  );
};

export default SubscriptionRequiredAlert; 